import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { DOOR_KEY } from "@/pages/Inbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TOPICS = ["Investor", "Partnership", "Pilot / retail", "Research", "Press", "Other"];

export const ContactForm = () => {
  const navigate = useNavigate();
  const openInbox = () => {
    sessionStorage.setItem(DOOR_KEY, "1");
    navigate("/inbox");
  };
  const [form, setForm] = useState({ name: "", email: "", topic: "", message: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("sent");
    } catch (err) {
      setStatus("idle");
      setError(err?.response?.data?.detail?.[0]?.msg || "Something went wrong. Try again.");
    }
  };

  return (
    <div className="on-dark bg-[#0D1B2A] text-[#F2F3F5]" id="contact" data-testid="contact-section">
      <div className="grid grid-cols-12 gap-x-6 px-6 md:px-10 py-24 md:py-36">
        <div className="col-span-12 md:col-span-5">
          <p className="eyebrow text-[#F2F3F5]/70 mb-6">Contact</p>
          <h2 className="display text-[2.2rem] sm:text-5xl lg:text-[4.2rem]" data-testid="contact-headline">
            Telltag
          </h2>
          <p className="body-copy mt-8 max-w-[34ch]">For investment, pilot and partnership enquiries.</p>
          <a
            href="mailto:support@telltag.co"
            className="mono text-sm inline-block mt-5 py-2 underline underline-offset-4 decoration-[#F2F3F5]/30 hover:decoration-[#F2F3F5] transition-colors"
            data-testid="contact-support-email-link"
          >
            support@telltag.co
          </a>
        </div>

        <div className="col-span-12 md:col-span-6 md:col-start-7 mt-16 md:mt-0">
          <AnimatePresence mode="wait">
            {status === "sent" ? (
              <motion.div
                key="sent"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                data-testid="contact-success"
              >
                <p className="eyebrow text-[#6B3FA0] mb-5">Received</p>
                <p className="display text-[1.8rem] sm:text-4xl">Thank you.</p>
              </motion.div>
            ) : (
              <motion.form key="form" onSubmit={submit} className="space-y-10" exit={{ opacity: 0 }} data-testid="contact-form">
                <label className="block">
                  <span className="eyebrow text-[#F2F3F5]/70">Name</span>
                  <input name="name" value={form.name} onChange={onChange} required className="field mt-2" placeholder="Name" data-testid="contact-name-input" />
                </label>
                <label className="block">
                  <span className="eyebrow text-[#F2F3F5]/70">Email</span>
                  <input type="email" name="email" value={form.email} onChange={onChange} required className="field mt-2" placeholder="Email" data-testid="contact-email-input" />
                </label>
                <label className="block">
                  <span className="eyebrow text-[#F2F3F5]/70">I am writing about</span>
                  <span className="relative block">
                    <select
                      name="topic"
                      value={form.topic}
                      onChange={onChange}
                      required
                      className="field mt-2 appearance-none pr-8 cursor-pointer"
                      style={{ color: form.topic ? undefined : "rgba(242,243,245,0.5)" }}
                      data-testid="contact-topic-select"
                    >
                      <option value="" disabled>
                        Choose a topic
                      </option>
                      {TOPICS.map((t) => (
                        <option key={t} value={t} className="text-[#0D1B2A]">
                          {t}
                        </option>
                      ))}
                    </select>
                    <span aria-hidden className="pointer-events-none absolute right-1 bottom-4 mono text-xs text-[#F2F3F5]/60">
                      ↓
                    </span>
                  </span>
                </label>
                <label className="block">
                  <span className="eyebrow text-[#F2F3F5]/70">Message</span>
                  <textarea name="message" value={form.message} onChange={onChange} required rows={3} className="field mt-2 resize-none" placeholder="Message" data-testid="contact-message-input" />
                </label>
                {error && <p className="mono text-xs text-[#E74C3C]" data-testid="contact-error">{error}</p>}
                <button type="submit" disabled={status === "sending"} className="btn-sharp text-[#F2F3F5]" style={{ "--btn-hover-text": "#0D1B2A" }} data-testid="contact-submit-button">
                  <span>{status === "sending" ? "Sending" : "Send"}</span>
                  <span aria-hidden>→</span>
                </button>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </div>

      <footer className="flex items-center justify-end px-6 md:px-10 py-8 border-t border-[#F2F3F5]/15 eyebrow text-[#F2F3F5]/65" data-testid="site-footer">
        <span className="flex items-center gap-4">
          © {new Date().getFullYear()}
          <button
            type="button"
            aria-label="Inbox"
            onClick={openInbox}
            className="relative block h-1.5 w-1.5 rounded-full bg-[#F2F3F5]/30 hover:bg-[#F2F3F5] transition-colors duration-500 before:absolute before:-inset-5 before:content-['']"
            data-testid="inbox-door-button"
          />
        </span>
      </footer>
    </div>
  );
};
